import React, { useEffect, useRef, useState } from "react";
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Image from "../components/image";
import * as d3 from 'd3';
import $ from 'jquery';
import './stackedBarTooltip.css';

function millisecondToMinutesAndSeconds(millis) {
  millis = parseInt(millis);
  if (isNaN(millis) === true) {
    return;
  }
  var minutes = Math.floor(millis / 60000);
  var seconds = ((millis % 60000) / 1000).toFixed(0);
  if (minutes < 1) {
    return seconds + "s";
  } else {
    return minutes + "m " + (seconds < 10 ? '' : '') + seconds + "s";
  }
}

const parseRunTime = (value) => {
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value < 1e12 ? value * 1000 : value);
    return isNaN(date.getTime()) ? null : date;
  }
  if (typeof value !== "string") return null;
  const embedded = value.match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
  const candidate = embedded ? `${embedded[1]}T${embedded[2]}` : value.trim();
  const date = new Date(candidate);
  return isNaN(date.getTime()) ? null : date;
};

const StackedBarChart = ({ data, connectorIcon, jobName, pipelineName, avg_duration, success_rate, runId, runDuration, theme = "light", showIdentity = true, showStats = true, fitWidth = false }) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const yAxisRef = useRef(null);
  const tooltipRef = useRef(null);
  const pinnedScrollRef = useRef(false);

  const [tooltipData, setTooltipData] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: '',
  });

  // Initial render and redraw on ResizeObserver
  useEffect(() => {
    if (!data || data.length === 0) return;
    pinnedScrollRef.current = false;
    const drawChart = () => {
      if (!data || data.length === 0) return;
      const reversedData = [...data].reverse();
      const canvas = chartRef.current;
      const container = containerRef.current;
      const yAxisCanvas = yAxisRef.current;
      if (!canvas || !container || !yAxisCanvas) return;
      const ctx = canvas.getContext("2d");
      const yCtx = yAxisCanvas.getContext("2d");
      if (!ctx || !yCtx) return;

      const margin = { top: 16, right: 8, bottom: fitWidth ? 36 : 50, left: fitWidth ? 28 : 0 };
      const measuredHeight = container.parentElement?.clientHeight || 0;
      const plotHeight = fitWidth ? Math.max(220, (measuredHeight > 80 ? measuredHeight : 300) - 16) : 300;
      if (plotHeight <= margin.top + margin.bottom) return;
      const chartHeight = plotHeight - margin.top - margin.bottom;
      // const containerWidth = container.offsetWidth;

      const actualBars = reversedData.length;
      const scrollParent = container.parentElement;
      const availableWidth = Math.max(scrollParent?.clientWidth || container.clientWidth || 0, 0);
      let barWidth = 28;
      let barSpacing = 18;
      let chartWidth = actualBars > 0 ? actualBars * (barWidth + barSpacing) - barSpacing : 0;
      const contentWidth = chartWidth + margin.left;
      let canvasWidth = Math.max(availableWidth, contentWidth);
      if (fitWidth && availableWidth > 0 && contentWidth > availableWidth) {
        canvasWidth = contentWidth;
      }
      if (scrollParent) {
        scrollParent.style.overflowX = canvasWidth > availableWidth + 1 ? "auto" : "hidden";
      }


      // Fix blurring: account for device pixel ratio
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvasWidth * dpr;
      canvas.height = (chartHeight + margin.top + margin.bottom) * dpr;
      canvas.style.width = canvasWidth + 'px';
      canvas.style.height = (chartHeight + margin.top + margin.bottom) + 'px';
      ctx.scale(dpr, dpr);

      yAxisCanvas.width = 60 * dpr;
      yAxisCanvas.height = (chartHeight + margin.top + margin.bottom) * dpr;
      yAxisCanvas.style.width = '60px';
      yAxisCanvas.style.height = (chartHeight + margin.top + margin.bottom) + 'px';
      yCtx.scale(dpr, dpr);

      ctx.clearRect(0, 0, canvasWidth, chartHeight + margin.top + margin.bottom);
      yCtx.clearRect(0, 0, 60, chartHeight + margin.top + margin.bottom);

      const yMax = Math.max(d3.max(reversedData, (d) => Number(d.value) || 0) || 0, 1);
      const yScale = d3.scaleLinear().domain([0, yMax]).range([chartHeight, 0]);

      const offset = canvasWidth > contentWidth + 1
        ? margin.left + (canvasWidth - contentWidth) / 2
        : margin.left;
      const xScale = d3
        .scaleBand()
        .domain([...Array(actualBars).keys()])
        .range([offset, offset + chartWidth])
        .paddingInner(0)
        .paddingOuter(0);

      const drawStageGradient = (ctx, x, y, w, h, index, stage, stages) => {
       // const isSuccess = stage.stage_status_text?.toLowerCase() === "succeeded";
        const gradient = ctx.createLinearGradient(x, y, x, y + h);

        if (stage.stage_status_text?.toLowerCase() === "succeeded") {
          const opacity = 0.3 + (index / (stages.length - 1 || 1)) * 0.7;
          const rgbaGreen = `rgba(43, 187, 173, ${opacity.toFixed(2)})`;
          const rgbaGreenDark = `rgba(102, 213, 196, ${opacity.toFixed(2)})`;
          gradient.addColorStop(0, rgbaGreen);
          gradient.addColorStop(1, rgbaGreenDark);
        } else if (stage.stage_status_text?.toLowerCase() === "failed") {
          gradient.addColorStop(0, "#FF5252");
          gradient.addColorStop(1, "#FF8A80");
        }
        else{
           gradient.addColorStop(0, "#8D6E63");
           gradient.addColorStop(1, "#A1887F");

        }

        return gradient;
      };

      const formatDurationLabel = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        const sec = ms / 1000;
        if (sec < 60) return `${sec.toFixed(1)}s`;
        if (sec < 3600) return `${(sec / 60).toFixed(1)}m`;
        return `${(sec / 3600).toFixed(1)}h`;
      };

      // Y-axis labels on separate canvas (right side)
      const dark = theme === "dark";
      const labelColor = dark ? "rgba(255,255,255,0.75)" : "#000";
      const gridColor = dark ? "rgba(255,255,255,0.14)" : "#ddd";
      const barStroke = dark ? "rgba(10, 8, 36, 0.55)" : "#fff";
      const yTicks = yScale.ticks();
      yCtx.strokeStyle = gridColor;
      yCtx.lineWidth = 1;
      yCtx.font = "10px sans-serif";
      yCtx.fillStyle = labelColor;
      yCtx.textAlign = "left";
      yCtx.textBaseline = "middle";

      yTicks.forEach(tick => {
        const y = margin.top + yScale(tick);
        // Grid lines across the full canvas width
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvasWidth, y);
        ctx.stroke();
        // Labels on Y-axis canvas
        const label = formatDurationLabel(tick);
        yCtx.textAlign = "right";
        yCtx.fillText(label, 56, y);
      });

      // Bars
      reversedData.forEach((d, i) => {
        const x = xScale(i);
        const stages = d.stages || [];
        const segmentHeight = stages.length > 0 ? d.value / stages.length : d.value;

        stages.forEach((stage, j) => {
          const y0 = segmentHeight * j;
          const y = margin.top + yScale(y0 + segmentHeight);
          const height = chartHeight - yScale(segmentHeight);
          const gradient = drawStageGradient(ctx, x, y, barWidth, height, j, stage, stages);

          ctx.fillStyle = gradient;
          ctx.strokeStyle = barStroke;
          ctx.lineWidth = 1;
          const radius = 4;
          ctx.beginPath();
          if (j === stages.length - 1) {
            ctx.moveTo(x, y + height);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.lineTo(x + barWidth - radius, y);
            ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
            ctx.lineTo(x + barWidth, y + height);
            ctx.lineTo(x, y + height);
          } else {
            ctx.rect(x, y, barWidth, height);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        });
      });

      const timeFormat = d3.timeFormat("%-d %b");
      ctx.font = "10px sans-serif";
      ctx.fillStyle = labelColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      let previousLabel = "";
      reversedData.forEach((d, i) => {
        const date = parseRunTime(d.time);
        if (!date) return;
        const label = timeFormat(date);
        if (!label || label.indexOf("NaN") !== -1 || label === previousLabel) return;
        previousLabel = label;
        const x = xScale(i) + barWidth / 2;
        if (fitWidth) {
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText(label, Math.max(x, 22), chartHeight + margin.top + 8);
        } else {
          ctx.save();
          ctx.translate(x, chartHeight + margin.top + 15);
          ctx.rotate(-Math.PI / 4);
          ctx.fillText(label, 0, 0);
          ctx.restore();
        }
      });

      // Tooltip mouse move
      canvas.onmousemove = (e) => {
        const canvasRect = canvas.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const mouseX = e.clientX - canvasRect.left;
        const mouseY = e.clientY - canvasRect.top;
        let tooltipX = e.clientX - containerRect.left + 10;
        const tooltipY = e.clientY - containerRect.top - 30;
        const maxRight = container.offsetWidth;
        let found = false;

        reversedData.forEach((d, i) => {
          const x = xScale(i);
          const stages = d.stages || [];
          const segmentHeight = stages.length > 0 ? d.value / stages.length : d.value;

          stages.forEach((stage, j) => {
            const y0 = segmentHeight * j;
            const y = margin.top + yScale(y0 + segmentHeight);
            const height = chartHeight - yScale(segmentHeight);

            if (
              mouseX >= x &&
              mouseX <= x + barWidth &&
              mouseY >= y &&
              mouseY <= y + height
            ) {
              found = true;

              if (tooltipX + 150 > maxRight) {
                tooltipX = e.clientX - containerRect.left - 160;
              }

              $("#run_id").html(d.run_id);
              $("#cur_run_duration").html(d.value_format);

              setTooltipData({
                visible: true,
                x: tooltipX,
                y: tooltipY,
                content: `
                  <strong>${stage.stage_name}</strong><br/>
                  Status: ${stage.stage_status_text}<br/>
                  Duration: ${millisecondToMinutesAndSeconds(stage.duration)}
                `,
              });
            }
          });
        });

        if (!found) {
          const last = reversedData[reversedData.length - 1];
          $("#run_id").html(last?.run_id || "");
          $("#cur_run_duration").html(last?.value_format || "");
          setTooltipData(prev => ({ ...prev, visible: false }));
        }
      };
    };
    const showLatestBars = () => {
      const scrollDiv = containerRef.current?.parentElement;
      if (!scrollDiv || pinnedScrollRef.current) return;
      if (scrollDiv.scrollWidth > scrollDiv.clientWidth + 1) {
        scrollDiv.scrollLeft = scrollDiv.scrollWidth;
      }
    };
    drawChart();
    showLatestBars();
    const observer = new ResizeObserver(() => {
      drawChart();
      showLatestBars();
    });
    const scrollParent = containerRef.current?.parentElement;
    const markUserScroll = () => {
      pinnedScrollRef.current = true;
    };
    if (scrollParent) {
      scrollParent.addEventListener("scroll", markUserScroll);
      observer.observe(scrollParent);
    }
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      observer.disconnect();
      if (scrollParent) scrollParent.removeEventListener("scroll", markUserScroll);
    };
  }, [data, theme, fitWidth]);

  return (
    <div className={fitWidth ? "tw-h-full tw-min-h-0 tw-flex tw-flex-col" : undefined}>
      {theme === "dark" ? (
        <style>{`
          .qd-stacked-scroll::-webkit-scrollbar { height: 6px; }
          .qd-stacked-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.28); border-radius: 8px; }
          .qd-stacked-scroll::-webkit-scrollbar-track { background: transparent; }
        `}</style>
      ) : null}
      <>
        {(showIdentity || showStats) ? <div className="chart-widget_stat-group">
          {showIdentity ? <Breadcrumb className="chart-widget_breadcrumb pl-0">
            <Breadcrumb.Item className="cw_breadcrumb-item">
              <Image className="mr-1" width="20px" height="20px" src={connectorIcon} />
              {pipelineName}
            </Breadcrumb.Item>
            <Breadcrumb.Item className="cw_breadcrumb-item">
              <i className="mr-1">
                <svg className="d-inline-block" width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M5.60478 5.61891H6.65343V6.66062H3.97499C3.64348 6.66062 3.37318 6.9262 3.37318 7.25191V8.38001H2.35643C2.02146 8.38001 1.75 8.64784 1.75 8.97584V11.6542C1.75 11.9833 2.0226 12.25 2.35643 12.25H5.08138C5.41638 12.25 5.68772 11.9822 5.68887 11.6542V8.97584C5.68887 8.64672 5.41627 8.38001 5.08244 8.38001H4.06623V7.34156H9.93414V8.38H8.91752C8.58255 8.38 8.31109 8.64784 8.31109 8.97583V11.6542C8.31109 11.9833 8.58369 12.25 8.91752 12.25H11.6435C11.9785 12.25 12.25 11.9822 12.25 11.6542V8.97583C12.25 8.64672 11.9774 8.38 11.6435 8.38H10.6285V7.25191C10.6285 6.9262 10.3582 6.66062 10.0266 6.66062H7.34649V5.61891H8.39542C8.71191 5.61891 8.96962 5.36581 8.96846 5.05486V2.31406C8.96846 2.00308 8.71086 1.75 8.39437 1.75H5.60478C5.28827 1.75 5.03069 2.0031 5.03069 2.31406V5.05486C5.03069 5.36583 5.28829 5.61891 5.60478 5.61891Z" fill="currentColor" />
              </svg>
            </i>
            {jobName}
          </Breadcrumb.Item>
        </Breadcrumb> : null}
        {showIdentity ? <div className="divider"></div> : null}
        {showStats ? <div className="chart-widget_stat mr-1">
          <span className="cw_stat-caption">Suc. Rate: </span>
          <strong className="cw_stat-value text-green">{success_rate}%</strong>
        </div> : null}
        {showStats ? <div className="chart-widget_stat mr-1">
          <span className="cw_stat-caption">Avg Duration: </span>
          <strong className="cw_stat-value">{avg_duration}</strong>
        </div> : null}
      </div> : null}
      {showStats ? <div className="chart-widget_stat-group">
        <div className="chart-widget_stat mr-1">
          <span className="cw_stat-caption">Run.ID </span>
          <strong className="cw_stat-value text-info">
            <i className="mx-1">
              <svg className="d-inline-block" width="16" height="16" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5007 5.80596V2.94596C10.5007 2.5793 10.2007 2.2793 9.83398 2.2793H7.16732C6.80065 2.2793 6.50065 2.5793 6.50065 2.94596V5.80596C6.50065 5.89263 6.53398 5.9793 6.60065 6.0393L8.26732 7.70596C8.40065 7.8393 8.60732 7.8393 8.74065 7.70596L10.4073 6.0393C10.4673 5.9793 10.5007 5.8993 10.5007 5.80596ZM5.36065 6.94596H2.50065C2.13398 6.94596 1.83398 7.24596 1.83398 7.61263V10.2793C1.83398 10.646 2.13398 10.946 2.50065 10.946H5.36065C5.44732 10.946 5.53398 10.9126 5.59398 10.846L7.26065 9.1793C7.39398 9.04596 7.39398 8.8393 7.26065 8.70596L5.59398 7.0393C5.53398 6.9793 5.45398 6.94596 5.36065 6.94596ZM6.50065 12.086V14.946C6.50065 15.3126 6.80065 15.6126 7.16732 15.6126H9.83398C10.2007 15.6126 10.5007 15.3126 10.5007 14.946V12.086C10.5007 11.9993 10.4673 11.9126 10.4007 11.8526L8.73398 10.186C8.60065 10.0526 8.39398 10.0526 8.26065 10.186L6.59398 11.8526C6.53398 11.9126 6.50065 11.9926 6.50065 12.086ZM11.4007 7.04596L9.73398 8.71263C9.60065 8.84596 9.60065 9.05263 9.73398 9.18596L11.4007 10.8526C11.4607 10.9126 11.5473 10.9526 11.634 10.9526H14.5007C14.8673 10.9526 15.1673 10.6526 15.1673 10.286V7.6193C15.1673 7.25263 14.8673 6.95263 14.5007 6.95263H11.6406C11.5473 6.94596 11.4673 6.9793 11.4007 7.04596Z" fill="currentColor"/>
              </svg>
            </i>
            <span id="run_id">{runId}</span>
          </strong>
        </div>
        <div className="chart-widget_stat mr-1">
          <span className="cw_stat-caption">Cur. Duration: </span>
          <strong id="cur_run_duration" className="cw_stat-value">{runDuration}</strong>
        </div>
        <div className="chart-widget_stat mr-1 d-none">
          <span className="cw_stat-caption">Data Volume: </span>
          <strong className="cw_stat-value">0.40 KB</strong>
        </div>
      </div> : null}
    </>

  <div style={{ position: 'relative', width: '100%', height: fitWidth ? 320 : 305 }}>
  <canvas ref={yAxisRef} style={{ position: 'absolute', right: 0, top: 0, width: '64px', height: '100%', zIndex: 2, pointerEvents: 'none', background: theme === 'dark' ? '#0A0824' : '#fff' }} />
  <div className={theme === "dark" ? "qd-stacked-scroll" : ""} style={{ position: 'absolute', left: 0, right: '64px', top: 0, bottom: 0, overflowX: 'auto', overflowY: 'hidden' }}>
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <canvas ref={chartRef} />
      {tooltipData.visible && (
        <div
          ref={tooltipRef}
          className="stacked-tooltip"
          style={{
            position: 'absolute',
            opacity: tooltipData.visible ? 0.9 : 0,
            pointerEvents: 'none',
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: '4px',
            top: tooltipData.y,
            left: tooltipData.x,
            transition: 'opacity 0.2s ease',
            fontSize: '13px',
            zIndex: 10,
          }}
          dangerouslySetInnerHTML={{ __html: tooltipData.content }}
        />
      )}
    </div>
  </div>
</div>
    </div>
  );
};

export default StackedBarChart;